import React from "react";
import { Box, Typography, Container, Button, Chip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EmailIcon from "@material-ui/icons/Email";
import LinkedInIcon from "@material-ui/icons/LinkedIn";
import LocationOnIcon from "@material-ui/icons/LocationOn";
import FlightTakeoffIcon from "@material-ui/icons/FlightTakeoff";
import AccessTimeIcon from "@material-ui/icons/AccessTime";
import { AnimatedSection } from "../content/AnimatedSection";
import Resume from "../../settings/resume.json";

const useStyles = makeStyles((theme) => ({
  contactSection: {
    backgroundColor: theme.palette.background.default,
    paddingTop: theme.spacing(10),
    paddingBottom: theme.spacing(6),
    marginTop: theme.spacing(8),
    borderTop: `1px solid ${theme.palette.divider}`,
    position: "relative",
    overflow: "hidden",
  },
  glowOrb: {
    position: "absolute",
    width: 400,
    height: 400,
    borderRadius: "50%",
    background:
      theme.palette.type === "dark"
        ? "radial-gradient(circle, rgba(0,191,191,0.07) 0%, transparent 70%)"
        : "radial-gradient(circle, rgba(0,191,191,0.05) 0%, transparent 70%)",
    top: "-80px",
    left: "50%",
    transform: "translateX(-50%)",
    pointerEvents: "none",
  },
  eyebrow: {
    display: "inline-flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: "0.75rem",
    fontWeight: 600,
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(2),
    padding: theme.spacing(0.5, 1.5),
    border: `1px solid ${theme.palette.primary.main}40`,
    borderRadius: 99,
    background:
      theme.palette.type === "dark"
        ? "rgba(0,191,191,0.08)"
        : "rgba(0,191,191,0.06)",
  },
  pulseDot: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
    display: "inline-block",
    animation: "$pulse 2s ease-in-out infinite",
  },
  "@keyframes pulse": {
    "0%, 100%": { opacity: 1, transform: "scale(1)" },
    "50%": { opacity: 0.5, transform: "scale(0.75)" },
  },
  heading: {
    fontWeight: 800,
    textAlign: "center",
    lineHeight: 1.15,
    marginBottom: theme.spacing(2.5),
    fontSize: "clamp(2rem, 5vw, 3rem)",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #ffffff 30%, #00bfbf 100%)"
        : "linear-gradient(135deg, #111111 30%, #00bfbf 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  subheading: {
    marginBottom: theme.spacing(5),
    textAlign: "center",
    color: theme.palette.text.secondary,
    maxWidth: 520,
    margin: "0 auto",
    lineHeight: 1.65,
  },
  serviceTagsRow: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(5),
    marginTop: theme.spacing(3),
  },
  serviceTag: {
    borderColor: `${theme.palette.primary.main}55`,
    color: theme.palette.text.secondary,
    fontSize: "0.78rem",
    fontWeight: 500,
    "& .MuiChip-label": {
      padding: theme.spacing(0, 1.5),
    },
  },
  ctaRow: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(5),
  },
  primaryBtn: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.type === "dark" ? "#0a0a0a" : "#ffffff",
    padding: theme.spacing(1.4, 3.5),
    fontSize: "0.95rem",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 99,
    transition: "all 0.25s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.light,
      transform: "translateY(-2px)",
      boxShadow: `0 8px 24px ${theme.palette.primary.main}50`,
    },
  },
  secondaryBtn: {
    border: `1.5px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    padding: theme.spacing(1.3, 3.5),
    fontSize: "0.95rem",
    fontWeight: 700,
    textTransform: "none",
    borderRadius: 99,
    transition: "all 0.25s ease",
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(0,191,191,0.1)"
          : "rgba(0,191,191,0.07)",
      transform: "translateY(-2px)",
      boxShadow: `0 6px 18px ${theme.palette.primary.main}30`,
    },
  },
  metaRow: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(6),
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    color: theme.palette.text.secondary,
    fontSize: "0.82rem",
  },
  metaIcon: {
    fontSize: "1rem",
    color: theme.palette.primary.main,
    opacity: 0.85,
  },
  metaDivider: {
    width: 4,
    height: 4,
    borderRadius: "50%",
    backgroundColor: theme.palette.divider,
  },
  copyright: {
    paddingTop: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
    color: theme.palette.text.secondary,
    fontSize: "0.8rem",
  },
}));

export const ContactFooter = () => {
  const classes = useStyles();

  const emailProfile = Resume.basics.profiles.find(
    (p) => p.network === "Google",
  );
  const linkedInProfile = Resume.basics.profiles.find(
    (p) => p.network === "LinkedIn",
  );

  return (
    <AnimatedSection>
      <Box className={classes.contactSection}>
        <Box className={classes.glowOrb} />
        <Container maxWidth="md">
          {/* Eyebrow */}
          <Box display="flex" justifyContent="center" mb={1}>
            <Box className={classes.eyebrow}>
              <span className={classes.pulseDot} />
              Open to new projects
            </Box>
          </Box>

          {/* Heading */}
          <Typography variant="h3" className={classes.heading}>
            Let's Build Something Great Together
          </Typography>

          {/* Subheading */}
          <Typography variant="body1" className={classes.subheading}>
            Whether you need a resilient cloud platform, a faster delivery
            pipeline, or an infrastructure audit — let's talk about what I can
            do for your team.
          </Typography>

          {/* Service Tags — driven from Resume.services */}
          {Resume.services && Resume.services.length > 0 && (
            <Box className={classes.serviceTagsRow}>
              {Resume.services.map((service) => (
                <Chip
                  key={service.name}
                  label={service.name}
                  variant="outlined"
                  size="small"
                  className={classes.serviceTag}
                />
              ))}
            </Box>
          )}

          {/* CTA Buttons */}
          <Box className={classes.ctaRow}>
            {emailProfile && (
              <Button
                href={emailProfile.url}
                startIcon={<EmailIcon />}
                className={classes.primaryBtn}
              >
                Send Me an Email
              </Button>
            )}
            {linkedInProfile && (
              <Button
                href={linkedInProfile.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<LinkedInIcon />}
                className={classes.secondaryBtn}
              >
                Connect on LinkedIn
              </Button>
            )}
          </Box>

          {/* Meta row: location · relocation · response time */}
          <Box className={classes.metaRow}>
            <Box className={classes.metaItem}>
              <LocationOnIcon className={classes.metaIcon} />
              <span>
                {Resume.basics.location.city}, {Resume.basics.location.country}
              </span>
            </Box>

            <Box className={classes.metaDivider} />

            <Box className={classes.metaItem}>
              <FlightTakeoffIcon className={classes.metaIcon} />
              <span>Open to relocation · UAE · KSA · EU</span>
            </Box>

            <Box className={classes.metaDivider} />

            <Box className={classes.metaItem}>
              <AccessTimeIcon className={classes.metaIcon} />
              <span>Typically responds within 24 hours</span>
            </Box>
          </Box>

          {/* Copyright */}
          <Box className={classes.copyright}>
            <Typography variant="body2">
              © 2026 {Resume.basics.name}. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </AnimatedSection>
  );
};
